import React, { useEffect, useState } from 'react';
import MainContainer from '../../../components/MainContainer';
import { headerDataImmo } from '../../../data/modules/logistic-header';
import { Dialog, FormControl, makeStyles, Typography } from '@material-ui/core';
import { 
  Button, 
  DialogActions, 
  DialogContent, 
  DialogTitle, 
  Grid, 
  Paper, 
  TextField, 
  FormLabel, 
  RadioGroup, 
  FormControlLabel, 
  Radio, 
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination
} from '@mui/material';
import Title from '../../../components/Title';
import Spacer from '../../../components/Spacer';
import LoadingSimpleBackdrop from '../../tools/LoadingSimpleBackdrop';
import Message from '../../tools/Message';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import IndemnitySettingService from '../../../services/IndemnitySettingService';
import { LoadingButton } from '@mui/lab';
import SaveIcon from '@mui/icons-material/Save';
import { headerComptable } from '../../../data/modules/comptable-header';

const useStyles = makeStyles((theme) => ({
  paper: {
    padding: theme.spacing(2),
    display: 'flex',
    overflow: 'auto',
    flexDirection: 'column',
    elevation: 2,
    borderRadius: '8px',
    marginTop: '2%',
  },
  btn: {
    margin: '1% 0.25% 1% 0.25%',
    fontWeight: 600,
  },
  tableContainer: {
    marginTop: '20px',
    maxHeight: '60vh',
  },
  tableHeadCell: {
    backgroundColor: theme.palette.common.black,
    color: theme.palette.common.white,
    fontWeight: 'bold',
  },
}));

const IndemnitySetting = () => {
  const classes = useStyles();
  const [rows, setRows] = useState([]);
  const [openLoadingSimpleBackdrop, setOpenLoadingSimpleBackdrop] = useState(false);
  const [message, setMessage] = useState({ open: false, color: 'green', messageInfo: '' });
  const { open, color, messageInfo } = message;

  const [selectedRow, setSelectedRow] = useState(null);
  const [openDialog, setOpenDialog] = useState(false);
  const [openDeleteDialog, setOpenDeleteDialog] = useState(false);
  const [loading, setLoading] = useState(false);
  
  // Pagination state
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(5);

  const [formValues, setFormValues] = useState({
    indemnity_id: 0,
    indemnity_type: '',
    daily_amount: '',
    is_default: '0',  // Par défaut sans nuitée
    is_default_nigth: '0'  // Par défaut avec nuitée
  });

  const fetchData = async () => {
    setOpenLoadingSimpleBackdrop(true);
    try {
      const response = await IndemnitySettingService.getIndemnitySettings();
      if (response && response.data && response.data.length > 0) {
        const formatted = response.data.map((item, index) => ({
          id: index + 1,
          indemnity_id: item.indemnity_id ?? item.id ?? index + 1,
          indemnity_type: item.indemnity_type ?? item.type ?? '',
          daily_amount: item.daily_amount ?? item.amount ?? 0,
          is_default: item.is_default ?? (item.default ? '1' : '0'),
          is_default_nigth: item.is_default_nigth ?? (item.is_default_nigth ? '1' : '0'),
          created_at: item.created_at ? new Date(item.created_at).toLocaleDateString() : '',
        }));
        setRows(formatted);
      } else {
        setRows([]);
      }
    } catch (err) {
      setMessage({ open: true, color: 'red', messageInfo: err.message || 'Erreur lors du chargement.' });
    } finally {
      setOpenLoadingSimpleBackdrop(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  const handleOpenDialog = () => {
    setSelectedRow(null);
    setFormValues({ 
      indemnity_id: 0, 
      indemnity_type: '', 
      daily_amount: '', 
      is_default: '0',
      is_default_nigth: '0' 
    });
    setOpenDialog(true);
  };

  const handleEdit = (row) => {
    setSelectedRow(row);
    setFormValues({
      indemnity_id: row.indemnity_id || 0,
      indemnity_type: row.indemnity_type || '',
      daily_amount: row.daily_amount || '',
      is_default: row.is_default || '0',
      is_default_nigth: row.is_default_nigth || '0',
    });
    setOpenDialog(true);
  };

  const handleDelete = (row) => {
    setSelectedRow(row);
    setFormValues({
      indemnity_id: row.indemnity_id || 0,
      indemnity_type: row.indemnity_type || '',
      daily_amount: row.daily_amount || '',
      is_default: row.is_default || '0',
      is_default_nigth: row.is_default_nigth || '0',
    });
    setOpenDeleteDialog(true);
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
    setSelectedRow(null);
    setFormValues({ 
      indemnity_id: 0, 
      indemnity_type: '', 
      daily_amount: '', 
      is_default: '0',
      is_default_nigth: '0' 
    });
  };

  const handleCloseDeleteDialog = () => {
    setOpenDeleteDialog(false);
    setSelectedRow(null);
    setFormValues({ 
      indemnity_id: 0, 
      indemnity_type: '', 
      daily_amount: '', 
      is_default: '0',
      is_default_nigth: '0' 
    });
  };

  const handleSave = async () => {
    // Validation
    if (!formValues.indemnity_type) {
      setMessage({ open: true, color: 'red', messageInfo: 'Veuillez saisir le libellé.' });
      return;
    }
    if (!formValues.daily_amount || formValues.daily_amount <= 0) {
      setMessage({ open: true, color: 'red', messageInfo: 'Veuillez saisir un montant valide.' });
      return;
    }

    try {
      setOpenLoadingSimpleBackdrop(true);
      setLoading(true);
      const payload = {
        indemnity_id: formValues.indemnity_id || undefined,
        indemnity_type: formValues.indemnity_type,
        daily_amount: Number(formValues.daily_amount),
        is_default: formValues.is_default,
        is_default_nigth: formValues.is_default_nigth
      };
      const result = await IndemnitySettingService.saveIndemnitySetting(payload);
      if (result) {
        setLoading(false);
        setMessage({ open: true, color: 'green', messageInfo: 'Enregistré avec succès.' });
        await fetchData();
      } else {
        setMessage({ open: true, color: 'red', messageInfo: 'Erreur lors de l\'enregistrement.' });
        setLoading(false);
      }
    } catch (err) {
      setMessage({ open: true, color: 'red', messageInfo: err.message || 'Erreur lors de l\'enregistrement.' });
      setLoading(false);
    } finally {
      setOpenLoadingSimpleBackdrop(false);
      handleCloseDialog();
      setLoading(false);
    }
  };

  const handleDeleteConfirm = async () => {
    try {
      setOpenLoadingSimpleBackdrop(true);
      const result = await IndemnitySettingService.deleteIndemnitySetting(formValues.indemnity_id);
      if (result && result.status) {
        setMessage({ open: true, color: 'green', messageInfo: 'Supprimé avec succès.' });
        await fetchData();
      } else {
        setMessage({ open: true, color: 'red', messageInfo: 'Erreur lors de la suppression.' });
      }
    } catch (err) {
      setMessage({ open: true, color: 'red', messageInfo: err.message || 'Erreur lors de la suppression.' });
    } finally {
      setOpenLoadingSimpleBackdrop(false);
      handleCloseDeleteDialog();
    }
  };

  const handleCloseMessage = () => setMessage({ ...message, open: false });

  // Fonction pour gérer le changement de "Par défaut sans nuitée"
  const handleDefaultChange = (value) => {
    if (value === "1") {
      // Si on sélectionne Oui pour sans nuitée, alors avec nuitée devient Non
      setFormValues({
        ...formValues,
        is_default: "1",
        is_default_nigth: "0"
      });
    } else {
      // Si on sélectionne Non pour sans nuitée
      setFormValues({
        ...formValues,
        is_default: "0"
      });
    }
  };

  // Fonction pour gérer le changement de "Par défaut avec nuitée"
  const handleDefaultNuiteChange = (value) => {
    if (value === "1") {
      // Si on sélectionne Oui pour avec nuitée, alors sans nuitée devient Non
      setFormValues({
        ...formValues,
        is_default_nigth: "1",
        is_default: "0"
      });
    } else {
      // Si on sélectionne Non pour avec nuitée
      setFormValues({
        ...formValues,
        is_default_nigth: "0"
      });
    }
  };

  // Fonction pour gérer le changement de "Autre" (les deux sont Non)
  const handleAutreChange = () => {
    setFormValues({
      ...formValues,
      is_default: "0",
      is_default_nigth: "0"
    });
  };

  // Pagination handlers
  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  // Calculate paginated rows
  const paginatedRows = rows.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage);

  return (
    <MainContainer {...headerComptable}>
      <LoadingSimpleBackdrop openLoadingSimpleBackdrop={openLoadingSimpleBackdrop} />
      <Message open={open} color={color} messageInfo={messageInfo} handleClose={handleCloseMessage} />

      <Grid container item xs={12}>
        <Button variant="contained" color="error" onClick={handleOpenDialog} style={{ fontWeight: 600 }}>
          CRÉER
        </Button>
        <Spacer x={2} />
      </Grid>

      <Grid item xl={12} md={12}>
        <Paper className={classes.paper} variant="outlined">
          <Title>Paramétrage des Indemnités</Title>
          <div style={{ padding: '4px' }}>
            <TableContainer component={Paper} className={classes.tableContainer}>
              <Table stickyHeader size="medium">
                <TableHead>
                  <TableRow>
                    <TableCell className={classes.tableHeadCell}>Libellé</TableCell>
                    <TableCell className={classes.tableHeadCell} align="left">Indemnité journalière (Ar)</TableCell>
                    <TableCell className={classes.tableHeadCell} align="center">Par défaut sans nuité</TableCell>
                    <TableCell className={classes.tableHeadCell} align="center">Par défaut avec nuité</TableCell>
                    <TableCell className={classes.tableHeadCell} align="center">Date de création</TableCell>
                    <TableCell className={classes.tableHeadCell} align="center">Actions</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {paginatedRows.length > 0 ? (
                    paginatedRows.map((row) => (
                      <TableRow key={row.id} hover>
                        <TableCell>{row.indemnity_type}</TableCell>
                        <TableCell align="left">{row.daily_amount.toLocaleString()} Ar</TableCell>
                        <TableCell align="center">
                          <Typography 
                            variant="body2" 
                            color={row.is_default === "1" ? 'success.main' : 'error.main'}
                            sx={{ fontWeight: 'medium' }}
                          >
                            {row.is_default === '1' ? 'Oui' : 'Non'}
                          </Typography>
                        </TableCell>
                        <TableCell align="center">
                          <Typography 
                            variant="body2" 
                            color={row.is_default_nigth === "1" ? 'success.main' : 'error.main'}
                            sx={{ fontWeight: 'medium' }}
                          >
                            {row.is_default_nigth === "1" ? 'Oui' : 'Non'}
                          </Typography>
                        </TableCell>
                        <TableCell align="center">{row.created_at}</TableCell>
                        <TableCell align="center">
                          <IconButton
                            onClick={() => handleEdit(row)}
                            size="small"
                            color="primary"
                            sx={{ marginRight: '8px' }}
                            title="Modifier"
                          >
                            <EditIcon />
                          </IconButton>
                          <IconButton
                            onClick={() => handleDelete(row)}
                            size="small"
                            color="error"
                            title="Supprimer"
                          >
                            <DeleteIcon />
                          </IconButton>
                        </TableCell>
                      </TableRow>
                    ))
                  ) : (
                    <TableRow>
                      <TableCell colSpan={6} align="center">
                        <Typography variant="body1" color="textSecondary">
                          Aucune donnée disponible
                        </Typography>
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
              <TablePagination
                rowsPerPageOptions={[5, 10, 25]}
                component="div"
                count={rows.length}
                rowsPerPage={rowsPerPage}
                page={page}
                onPageChange={handleChangePage}
                onRowsPerPageChange={handleChangeRowsPerPage}
                labelRowsPerPage="Lignes par page:"
              />
            </TableContainer>
          </div>
        </Paper>
      </Grid>

      <Dialog open={openDialog} onClose={handleCloseDialog} aria-labelledby="form-dialog-title" maxWidth="sm" fullWidth>
        <DialogTitle id="form-dialog-title">
          {selectedRow ? 'Modifier une indemnité' : 'Ajouter une indemnité'}
        </DialogTitle>
        <DialogContent>
          <Spacer y={2} />
          <Grid container spacing={2}>
            <Grid item xs={12}>
              <TextField
                fullWidth
                size="small"
                label="Libellé"
                variant="outlined"
                value={formValues.indemnity_type}
                onChange={(e) => setFormValues({ ...formValues, indemnity_type: e.target.value })}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                size="small"
                type="number"
                label="Indemnité journalière (Ar)"
                variant="outlined"
                value={formValues.daily_amount}
                onChange={(e) => setFormValues({ ...formValues, daily_amount: e.target.value })}
              />
            </Grid>

            <Grid item xs={12}>
              <Grid container spacing={2} alignItems="center">
                <Grid item xs={5}>
                  <FormLabel>Par défaut sans nuitée :</FormLabel>
                </Grid>
                <Grid item xs={7}>
                  <FormControl component="fieldset" size="small">
                    <RadioGroup
                      value={formValues.is_default}
                      onChange={(e) => handleDefaultChange(e.target.value)}
                      row
                    >
                      <FormControlLabel value="1" control={<Radio />} label="Oui" />
                      <FormControlLabel value="0" control={<Radio />} label="Non" />
                    </RadioGroup>
                  </FormControl>
                </Grid>
              </Grid>
            </Grid>

            <Grid item xs={12}>
              <Grid container spacing={2} alignItems="center">
                <Grid item xs={5}>
                  <FormLabel>Par défaut avec nuitée :</FormLabel>
                </Grid>
                <Grid item xs={7}>
                  <FormControl component="fieldset" size="small">
                    <RadioGroup
                      value={formValues.is_default_nigth}
                      onChange={(e) => handleDefaultNuiteChange(e.target.value)}
                      row
                    >
                      <FormControlLabel value="1" control={<Radio />} label="Oui" />
                      <FormControlLabel value="0" control={<Radio />} label="Non" />
                    </RadioGroup>
                  </FormControl>
                </Grid>
              </Grid>
            </Grid>

            <Grid item xs={12}>
              <Grid container spacing={2} alignItems="center">
                <Grid item xs={5}>
                  <FormLabel>Autre (Aucun par défaut) :</FormLabel>
                </Grid>
                <Grid item xs={7}>
                  <Button
                    variant={formValues.is_default === "0" && formValues.is_default_nigth === "0" ? "contained" : "outlined"}
                    color="primary"
                    onClick={handleAutreChange}
                    size="small"
                  >
                    Appliquer Aucun
                  </Button>
                  <Typography variant="caption" display="block" color="textSecondary" style={{ marginTop: '5px' }}>
                    {formValues.is_default === "0" && formValues.is_default_nigth === "0" 
                      ? "✓ Actif : Ni sans nuitée ni avec nuitée n'est par défaut" 
                      : "Cliquez pour désactiver tous les par défaut"}
                  </Typography>
                </Grid>
              </Grid>
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog} color="error" variant="contained" size='small'>
            Annuler
          </Button>
          <LoadingButton
            onClick={handleSave}
            loading={loading}
            loadingPosition="start" 
            startIcon={<SaveIcon />}
            color="info"
            variant="contained"
            size="small"
          >
            Enregistrer
          </LoadingButton>
        </DialogActions>
      </Dialog>

      <Dialog open={openDeleteDialog && !!selectedRow} onClose={handleCloseDeleteDialog} maxWidth="sm" fullWidth>
        <DialogTitle id="form-dialog-title">Supprimer une indemnité</DialogTitle>
        <DialogContent>
          <Spacer y={2} />
          <Typography>
            Êtes-vous sûr de vouloir supprimer l'indemnité
            {selectedRow ? ` "${selectedRow.indemnity_type}"` : ''} ?
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDeleteDialog} color="error" variant="contained" size='small'>
            Annuler
          </Button>
          <Button onClick={handleDeleteConfirm} color="info" variant="contained" size='small'>
            Confirmer
          </Button>
        </DialogActions>
      </Dialog>
    </MainContainer>
  );
};

export default IndemnitySetting;